import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Fallback state for browsers that do not support CSS :has().
   * @type {MutationObserver | null}
   */
  #fallbackStateObserver = null;

  /** @type {(() => void) | null} */
  #fallbackCleanup = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Mirrors the structural/interactive :has() selectors in header.liquid with
   * classes and attributes. This is intentionally cheap and only runs for the
   * header, so unsupported browsers get the same layout without evaluating :has().
   */
  #syncHasFallbackState = () => {
    const columnsList = [...this.querySelectorAll('.header__columns')];
    const headerSection = this.closest('.header-section');
    const headerGroup = document.querySelector('#header-group');

    columnsList.forEach((columns) => {
      const hasCenter = !!columns.querySelector('.header__column--center');
      const hasColumns = !!columns.querySelector('.header__column');
      const hasActions = !!columns.querySelector('header-actions');
      const hasAccount = !!columns.querySelector('shopify-account');
      const hasMenuLeft = !!columns.querySelector('.header__column--left header-menu');
      const hasMenuRight = !!columns.querySelector('.header__column--right header-menu');

      columns.classList.toggle('header__columns--has-center', hasCenter);
      columns.classList.toggle('header__columns--no-center', !hasCenter);
      columns.classList.toggle('header__columns--has-columns', hasColumns);
      columns.classList.toggle('header__columns--no-columns', !hasColumns);
      columns.classList.toggle('header__columns--has-actions', hasActions);
      columns.classList.toggle('header__columns--no-actions', !hasActions);
      columns.classList.toggle('header__columns--has-account', hasAccount);
      columns.classList.toggle('header__columns--no-account', !hasAccount);
      columns.classList.toggle('header__columns--menu-left', hasMenuLeft);
      columns.classList.toggle('header__columns--menu-right', hasMenuRight);

      columns.querySelectorAll('.header__column').forEach((column) => {
        const menu = column.querySelector('header-menu');
        const actions = column.querySelector('header-actions');
        const menuIcon = column.querySelector('.header__icon--menu');
        const menuHovered = !!menu?.matches(':hover');
        const actionsHovered = !!column.querySelector('.header-actions__action:hover');
        const iconHovered = !!menuIcon?.matches(':hover');

        column.classList.toggle('header__column--has-menu', !!menu);
        column.classList.toggle('header__column--has-actions', !!actions);
        column.classList.toggle('header__column--is-hovered', menuHovered || actionsHovered || iconHovered);
        column.classList.toggle('header__column--menu-hovered', menuHovered);
        column.classList.toggle('header__column--actions-hovered', actionsHovered);
        column.classList.toggle('header__column--menu-icon-hovered', iconHovered);
      });
    });

    const menuLinks = [...this.querySelectorAll('.menu-list__link')];
    const megaLists = [...this.querySelectorAll('.mega-menu__list')];
    const menuHovered = menuLinks.some((link) => link.matches(':hover')) || megaLists.some((list) => list.matches(':hover'));
    const menuOpen = menuLinks.some((link) => link.getAttribute('aria-expanded') === 'true');
    const overflowOpen = !!this.querySelector(
      ".menu-list__list-item[slot='overflow'] .menu-list__link[aria-expanded='true']"
    );
    const drawerOpen = !!this.querySelector('#Details-menu-drawer-container[open]');
    const menuActive = menuHovered || menuOpen || overflowOpen;

    this.classList.toggle('header--fallback-menu-hover', menuHovered);
    this.classList.toggle('header--fallback-menu-open', menuOpen || overflowOpen);
    this.classList.toggle('header--fallback-menu-active', menuActive);
    this.classList.toggle('header--fallback-drawer-open', drawerOpen);

    this.querySelectorAll('.header__row').forEach((row) => {
      const active = !!row.querySelector(
        ".mega-menu__list:hover, .menu-list__link:hover, .menu-list__link[aria-expanded='true'], .menu-list__list-item[slot='overflow'] .menu-list__link[aria-expanded='true']"
      );
      row.classList.toggle('header__row--fallback-submenu-active', active);
    });

    if (headerSection) {
      const stickyMode = this.getAttribute('sticky');
      const stickyActive = stickyMode === 'always' ||
        (stickyMode === 'scroll-up' && this.dataset.stickyState === 'active');
      headerSection.classList.toggle('header-section--fallback-sticky', stickyActive);
      headerSection.classList.toggle('header-section--fallback-sticky-always', stickyMode === 'always');
      headerSection.classList.toggle('header-section--fallback-transparent', this.hasAttribute('transparent'));
    }

    headerGroup?.classList.toggle('header-group--fallback-sticky', this.hasAttribute('sticky'));
  };

  #setupHasFallback = () => {
    this.#syncHasFallbackState();

    const events = ['mouseover', 'mouseout', 'focusin', 'focusout'];
    events.forEach((eventName) => this.addEventListener(eventName, this.#syncHasFallbackState));

    this.#fallbackStateObserver = new MutationObserver(() => this.#syncHasFallbackState());
    this.#fallbackStateObserver.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-expanded', 'open'],
    });

    window.addEventListener('resize', this.#syncHasFallbackState, { passive: true });

    return () => {
      events.forEach((eventName) => this.removeEventListener(eventName, this.#syncHasFallbackState));
      this.#fallbackStateObserver?.disconnect();
      this.#fallbackStateObserver = null;
      window.removeEventListener('resize', this.#syncHasFallbackState);
    };
  };

  connectedCallback() {
    super.connectedCallback();
    this.#fallbackCleanup?.();
    this.#fallbackCleanup = this.#setupHasFallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#fallbackCleanup?.();
    this.#fallbackCleanup = null;
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
